import React, { type FC, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import { FavoritesCategory } from '../../api/services/favorites/types';

import { API, useFetch } from 'app/api';
import { Icon, IconSize } from 'app/design-system/components/Icon';
import { useTranslation } from 'app/lang';

import { openLoginDialog } from '../../modules/Signing/actions';
import { selectIsLoggedIn } from '../../redux/selectors/auth';

import dictionary from './FavoritesIcon.dictionary.json';

import styles from './FavoritesIcon.module.scss';

type FavoritesIconProps = {
  matchId: number;
  dataGaEvent: string;
  title?: string;
  className?: string;
  size?: IconSize;
  needAnimation?: boolean;
};

export const FavoritesIcon: FC<FavoritesIconProps> = ({
  matchId,
  dataGaEvent,
  title,
  className,
  size = IconSize.MEDIUM,
  needAnimation = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const animationTimerId = useRef<NodeJS.Timer | null>(null);
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const [active, setActive] = useState(false);
  const [animating, setAnimating] = useState(false);
  const positiveAction = isLoggedIn
    ? t(dictionary.addToFavourite)
    : t(dictionary.loginAndAddToFavorite);
  const defaultTitle = active ? t(dictionary.removeFromFavourite) : positiveAction;

  const wrapperClassNames = classNames(styles.root, {
    [styles.favoriteIconActive]: active,
  }, className);

  const {
    error: addToFavoritesError,
    makeRequest: addToFavorites,
  } = useFetch(API.favorites.addToFavorites, { skipSSR: true });

  const {
    makeRequest: removeFromFavorites,
  } = useFetch(API.favorites.removeFromFavorites, { skipSSR: true });

  const handleLogin = useCallback(() => {
    if (!isLoggedIn) {
      dispatch(openLoginDialog());
    }
  }, [isLoggedIn]);

  const handleFavorite = useCallback(() => {
    const payload = { category: FavoritesCategory.MATCH, categoryId: matchId };

    if (active) {
      removeFromFavorites(payload);
      setActive(false);
    }
    else {
      addToFavorites(payload);
      setActive(true);
    }

    if (!active && !addToFavoritesError) {
      setAnimating(true);
    }
  }, [matchId, active, addToFavoritesError]);

  useEffect(() => {
    if (animationTimerId.current) {
      clearTimeout(animationTimerId.current);
      animationTimerId.current = null;
    }
    if (animating && !animationTimerId.current) {
      animationTimerId.current = setTimeout(() => {
        setAnimating(false);
      }, 1000);
    }
  }, [animating, animationTimerId.current]);

  return (
    <button type="button"
            title={title || defaultTitle}
            className={wrapperClassNames}
            onClick={isLoggedIn ? handleFavorite : handleLogin}
            data-ga-event={dataGaEvent}
            data-ga-sport-match={matchId}>
      <Icon icon={active ? 'favorite-active' : 'favorite'}
            size={size} />
      {(needAnimation && active) && (
        <p className={classNames(styles.animationTitle, {
          [styles.animationTitleClose]: active && !animating,
        })}>
          {t(dictionary.animationTitle)}
        </p>
      )}
    </button>
  );
};
